import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useEntitlements } from "@/modules/useEntitlements";
import { WORK_MODULES } from "@/modules/workModuleCore";
import {
  LIMITS_NOT_CONFIGURED_NOTICE,
  PARTIAL_DATA_NOTICE,
  mapCompradorOverviewToView,
} from "@/modules/despacho/compradorOverviewAdapters";
import {
  COMPRADOR_DESIGN_NOTICE,
  MOCK_NOTAS_SIGUIENTE_FASE,
} from "@/modules/despacho/compradorDespachoMockData";
import {
  DespachoCountBadge,
  DespachoInfoCallout,
  DespachoSectionCard,
  DespachoStatusBadge,
} from "@/components/despacho/DespachoUi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertCircle, Info, Loader2 } from "lucide-react";

function InfoField({ label, value, mono = false }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold m-0">{label}</p>
      <p className={`text-sm text-[#0F2D5C] m-0 mt-0.5 ${mono ? "font-mono text-xs" : "font-medium"}`}>{value}</p>
    </div>
  );
}

/**
 * Vista read-only del comprador/despacho — datos reales desde GET /api/system/comprador-overview.
 */
export default function CompradorDespachoOverview({ showDesignBanner = true }) {
  const { user } = useAuth();
  const { loading: entLoading, hasEntitlement, isPlatformDesigner } = useEntitlements();
  const [overview, setOverview] = useState(null);
  const [fetchState, setFetchState] = useState("loading");
  const [fetchError, setFetchError] = useState("");

  const canViewOverview = Boolean(
    isPlatformDesigner
    || user?.is_admin_owner
    || user?.role === "buyer_admin"
    || hasEntitlement("despacho"),
  );

  const loadOverview = useCallback(async () => {
    if (!canViewOverview && !entLoading) {
      setFetchState("forbidden");
      return;
    }
    if (entLoading) return;
    setFetchState("loading");
    setFetchError("");
    try {
      const { data } = await api.get("/system/comprador-overview", { timeout: 30000 });
      setOverview(data);
      setFetchState("ready");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setFetchState("forbidden");
      } else {
        setFetchState("error");
        setFetchError(err?.response?.data?.detail || err?.message || "No se pudo cargar el resumen.");
      }
    }
  }, [canViewOverview, entLoading]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const view = useMemo(() => mapCompradorOverviewToView(overview), [overview]);

  if (entLoading || fetchState === "loading") {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600 py-8" data-testid="comprador-overview-loading">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Cargando resumen del comprador…
      </div>
    );
  }

  if (fetchState === "forbidden") {
    return (
      <div
        className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
        role="alert"
        data-testid="comprador-overview-forbidden"
      >
        No tiene permiso para ver el resumen del comprador/despacho en este espacio.
      </div>
    );
  }

  if (fetchState === "error") {
    return (
      <div className="space-y-3" data-testid="comprador-overview-error">
        <div className="rounded-sm border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-950 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
          <p className="m-0">{fetchError}</p>
        </div>
        <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={loadOverview}>
          Reintentar
        </Button>
      </div>
    );
  }

  const comprador = view?.comprador || {};
  const modulos = view?.modulos || [];
  const limits = view?.limits || {};
  const limitsNotConfigured = view?.limitsNotConfigured !== false;
  const estadoTone = view?.estadoTone || "neutral";
  const notes = [...(view?.notes || []), ...(limitsNotConfigured ? [LIMITS_NOT_CONFIGURED_NOTICE] : [])];

  return (
    <div className="space-y-6" data-testid="comprador-despacho-overview">
      {showDesignBanner ? (
        <div
          className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 flex items-start gap-2"
          role="status"
          data-testid="comprador-design-banner"
        >
          <Info className="h-4 w-4 shrink-0 mt-0.5 text-amber-700" aria-hidden />
          <p className="m-0 text-xs leading-relaxed">
            {COMPRADOR_DESIGN_NOTICE} {PARTIAL_DATA_NOTICE}
          </p>
        </div>
      ) : null}

      {limitsNotConfigured ? (
        <div
          className="rounded-sm border border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-700"
          data-testid="comprador-limits-not-configured"
        >
          {LIMITS_NOT_CONFIGURED_NOTICE}
        </div>
      ) : null}

      <DespachoSectionCard title="Datos del comprador" description="Espacio de trabajo del despacho comprador (titular o administrador del comprador).">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <InfoField label="Nombre comercial del despacho" value={comprador.nombre_comercial} />
          <InfoField label="RFC" value={comprador.rfc || "—"} mono />
          <InfoField label="Correo principal" value={comprador.correo_principal} />
          <InfoField label="Usuario dueño" value={comprador.usuario_dueno} />
          <InfoField label="Plan contratado" value={comprador.plan} />
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold m-0">Estado comercial</p>
            <div className="mt-1">
              <DespachoCountBadge value={comprador.estado_comercial} tone={estadoTone} />
            </div>
          </div>
        </div>
      </DespachoSectionCard>

      <DespachoSectionCard
        title="Módulos contratados"
        description="Estado «Contratado» según entitlements de la instalación (solo lectura)."
      >
        <div className="overflow-x-auto border border-slate-200 rounded-sm bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Módulo</TableHead>
                <TableHead>Contratado</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Inicio</TableHead>
                <TableHead>Vencimiento</TableHead>
                <TableHead className="text-right">Días rest.</TableHead>
                <TableHead>Límite</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {modulos.map((mod) => (
                <TableRow key={mod.id}>
                  <TableCell className="font-medium text-[#0F2D5C] text-xs">{mod.label}</TableCell>
                  <TableCell>
                    <DespachoStatusBadge variant={mod.contratado ? "default" : "secondary"}>
                      {mod.contratado ? "Sí" : "No"}
                    </DespachoStatusBadge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`font-normal text-[10px] ${
                        mod.estado === "Activo"
                          ? "bg-sky-50 text-sky-900 border-sky-200"
                          : mod.estado === "Vencido"
                            ? "bg-rose-50 text-rose-900 border-rose-200"
                            : mod.estado === "Suspendido"
                              ? "bg-amber-50 text-amber-900 border-amber-200"
                              : "bg-slate-50 text-slate-600 border-slate-200"
                      }`}
                    >
                      {mod.estado}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">{mod.fecha_inicio}</TableCell>
                  <TableCell className="text-xs text-slate-600">{mod.fecha_vencimiento}</TableCell>
                  <TableCell className="text-right tabular-nums text-xs">{mod.dias_restantes}</TableCell>
                  <TableCell className="text-xs text-slate-500">{mod.limite || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <p className="text-[10px] text-slate-500 m-0">
          Fuente «Contratado»: <code className="text-[9px]">GET /api/system/comprador-overview</code> → entitlements.
        </p>
      </DespachoSectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DespachoSectionCard title="Vigencias y vencimientos" description="Resumen global del espacio de trabajo (licencia o periodo de prueba).">
          <div className="grid grid-cols-2 gap-3">
            <InfoField label="Fecha de inicio" value={comprador.fecha_inicio} />
            <InfoField label="Fecha de vencimiento" value={comprador.fecha_vencimiento} />
            <InfoField
              label={limitsNotConfigured ? "Días restantes" : "Días restantes (global)"}
              value={String(comprador.dias_restantes)}
            />
            <InfoField label="Modo suscripción" value={view?.subscriptionMode || "—"} />
          </div>
          <DespachoInfoCallout>
            Vigencia global y módulos contratados provienen del servidor. Las fechas por módulo y límites comerciales
            permanecen sin configurar hasta una fase posterior.
          </DespachoInfoCallout>
        </DespachoSectionCard>

        <DespachoSectionCard
          title="Límites comerciales — Gestión Despacho"
          description={limitsNotConfigured ? "No configurados en licencia (solo lectura)." : "Límites del espacio de trabajo."}
        >
          {limitsNotConfigured ? (
            <p className="text-sm text-slate-600 m-0" data-testid="comprador-limits-placeholder">
              Sin límites comerciales definidos en la licencia actual.
            </p>
          ) : (
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-600">Auxiliares autorizados</span>
                <DespachoCountBadge value={limits.max_auxiliares ?? "—"} />
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-600">Auxiliares activos</span>
                <DespachoCountBadge value={limits.auxiliares_activos ?? "—"} tone="info" />
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-600">Clientes operativos autorizados</span>
                <DespachoCountBadge value={limits.max_clientes_despacho ?? "—"} />
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-600">Clientes operativos registrados</span>
                <DespachoCountBadge value={limits.clientes_despacho_registrados ?? "—"} tone="info" />
              </div>
            </div>
          )}
          {!limitsNotConfigured && limits.source === "mock" ? (
            <p className="text-[10px] text-slate-500 m-0 mt-2">Fuente: ejemplo de diseño</p>
          ) : null}
        </DespachoSectionCard>
      </div>

      <DespachoSectionCard title="Estado de acceso" description="Quién ve qué en esta fase del producto.">
        <ul className="text-sm text-slate-700 space-y-2 m-0 list-disc pl-4 marker:text-[#3DA873]">
          <li>
            <strong>Distribuidor / plataforma:</strong> catálogo compradores, cartera licencias — sin administración
            unificada de límites por módulo todavía (TODO).
          </li>
          <li>
            <strong>Comprador / despacho:</strong> Panel General, esta vista read-only, Configuración.
          </li>
          <li>
            <strong>Cliente operativo del despacho:</strong> colección <code className="text-[10px]">despacho_clients</code>.
          </li>
          <li>
            <strong>Cliente fiscal Visor SAT:</strong> colección <code className="text-[10px]">clients</code>.
          </li>
        </ul>
        <div className="pt-3 flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm" className="rounded-sm">
            <Link to="/">Panel General</Link>
          </Button>
          <Button asChild variant="outline" size="sm" className="rounded-sm">
            <Link to="/despacho/configuracion-modulo">Parámetros del módulo</Link>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm"
            disabled
            title="Disponible en Fase Plataforma"
            data-testid="comprador-activar-modulo-btn"
          >
            Activar módulo
          </Button>
        </div>
      </DespachoSectionCard>

      <DespachoSectionCard title="Notas" description="Información del servidor y alcance de esta fase.">
        <ul className="text-xs text-slate-600 space-y-1.5 m-0 list-disc pl-4 marker:text-[#3DA873]">
          {notes.map((nota) => (
            <li key={nota}>{nota}</li>
          ))}
          {MOCK_NOTAS_SIGUIENTE_FASE.slice(0, 2).map((nota) => (
            <li key={nota}>{nota}</li>
          ))}
        </ul>
        <p className="text-[10px] text-slate-500 m-0 mt-3">
          Módulos work IDs: {Object.values(WORK_MODULES).filter((id) => typeof id === "string").join(", ")}
        </p>
      </DespachoSectionCard>
    </div>
  );
}
